import { useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import { describeNode, type SegmentNode } from '@mailflow/core/segments';
import { listsQuery } from '../lib/contacts.js';
import { segmentQuery, previewQuery, useCreateSegment, useUpdateSegment } from '../lib/segments.js';
import { SegmentTree } from '../components/SegmentTree.js';
import { useDebounced } from '../lib/useDebounced.js';
import { api } from '../lib/api.js';

const EMPTY: SegmentNode = { op: 'AND', children: [] };

export function SegmentBuilderPage({ mode }: { mode: 'create' | 'edit' }) {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id;
  const navigate = useNavigate();

  const existing = useQuery({ ...segmentQuery(id ?? ''), enabled: mode === 'edit' && Boolean(id) });
  const lists = useQuery(listsQuery);
  const tags = useQuery({
    queryKey: ['tags'],
    queryFn: ({ signal }) =>
      api.get<{ items: Array<{ id: string; name: string }> }>('/api/tags', signal),
  });
  const campaigns = useQuery({
    queryKey: ['campaigns'],
    queryFn: ({ signal }) =>
      api.get<{ items: Array<{ id: string; name: string }> }>('/api/campaigns', signal),
  });

  const [name, setName] = useState('');
  const [definition, setDefinition] = useState<SegmentNode>(EMPTY);
  const [loaded, setLoaded] = useState(false);

  // Beim Laden eines bestehenden Segments genau einmal uebernehmen — sonst
  // wuerde jede Eingabe vom Serverstand ueberschrieben.
  if (mode === 'edit' && existing.data && !loaded) {
    setName(existing.data.segment.name);
    setDefinition(existing.data.segment.definition);
    setLoaded(true);
  }

  const create = useCreateSegment();
  const update = useUpdateSegment(id ?? '');

  /*
   * 400 ms Verzoegerung: waehrend der Nutzer noch tippt, soll nicht bei jedem
   * Zeichen eine Auswertung laufen. keepPreviousData haelt das letzte
   * Ergebnis sichtbar, damit die Zahl nicht flackert.
   */
  const debounced = useDebounced(definition, 400);
  const preview = useQuery({ ...previewQuery(debounced), placeholderData: keepPreviousData });

  const context = useMemo(
    () => ({
      lists: lists.data?.items.map((l) => ({ id: l.id, name: l.name })) ?? [],
      tags: tags.data?.items ?? [],
      campaigns: campaigns.data?.items.map((c) => ({ id: c.id, name: c.name })) ?? [],
    }),
    [lists.data, tags.data, campaigns.data],
  );

  const percent =
    preview.data && preview.data.totalContacts > 0
      ? Math.round((preview.data.total / preview.data.totalContacts) * 1000) / 10
      : 0;

  async function save() {
    if (mode === 'create') {
      const result = await create.mutateAsync({ name, definition });
      await navigate({ to: '/segmente/$id', params: { id: result.segment.id } });
    } else {
      await update.mutateAsync({ name, definition });
    }
  }

  const busy = create.isPending || update.isPending;
  const saveError = create.error ?? update.error;

  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/segmente">Segmente</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{mode === 'create' ? 'Neu' : name || 'Bearbeiten'}</span>
      </nav>

      {/*
        Die Trefferzahl steht dauerhaft im Blick, nicht hinter einem Knopf.
        Wer eine Regel aendert, sieht sofort, was sie bewirkt.
      */}
      <div className="preview-bar" aria-live="polite">
        <div>
          <strong data-numeric style={{ fontSize: 'var(--text-xl)' }}>
            {preview.isError ? '—' : (preview.data?.total.toLocaleString('de-CH') ?? '…')}
          </strong>
          <span className="muted">
            {' '}
            von {preview.data?.totalContacts.toLocaleString('de-CH') ?? '…'} Kontakten
            {preview.data ? ` (${percent.toLocaleString('de-CH')} %)` : ''}
          </span>
        </div>
        <span className="muted text-sm">
          {preview.isFetching
            ? 'wird ausgewertet …'
            : preview.data
              ? `${preview.data.durationMs} ms`
              : ''}
        </span>
      </div>

      {preview.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {preview.error instanceof Error
            ? preview.error.message
            : 'Die Vorschau konnte nicht berechnet werden.'}
        </div>
      )}

      <section className="card">
        <div className="card__body stack">
          <Field
            label="Name des Segments"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Nicht-Öffner Newsletter März"
          />
        </div>
      </section>

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Bedingungen</h2>
          <SegmentTree node={definition} context={context} onChange={setDefinition} />
          <p className="muted text-sm">
            <strong>Als Satz:</strong>{' '}
            {describeNode(definition, {
              listNames: Object.fromEntries(context.lists.map((l) => [l.id, l.name])),
              tagNames: Object.fromEntries(context.tags.map((t) => [t.id, t.name])),
              campaignNames: Object.fromEntries(context.campaigns.map((c) => [c.id, c.name])),
            })}
          </p>
        </div>
      </section>

      {preview.data && preview.data.sample.length > 0 && (
        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Beispielkontakte</h2>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">E-Mail</th>
                    <th scope="col">Name</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.sample.map((contact) => (
                    <tr key={contact.id}>
                      <td>{contact.email}</td>
                      <td>
                        {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td>{contact.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {saveError && (
        <div className="alert" data-tone="danger" role="alert">
          {saveError instanceof Error ? saveError.message : 'Speichern fehlgeschlagen.'}
        </div>
      )}

      <div className="dialog__actions">
        <Link to="/segmente" className="btn" data-variant="secondary">
          Abbrechen
        </Link>
        <Button onClick={save} loading={busy} disabled={name.trim().length === 0}>
          {mode === 'create' ? 'Segment anlegen' : 'Änderungen speichern'}
        </Button>
      </div>
    </div>
  );
}
